import { useState, useRef, useEffect, useMemo } from "react";
import { Modal, Select, DatePicker, Collapse } from "antd";
import { CloseOutlined, WarningOutlined, ArrowRightOutlined } from "@ant-design/icons";
import SelectJobIcon from "../../../../assets/icons/SelectJobIcon.svg";
import EditIcon from "../../../../assets/icons/EditIcon.svg";
import { useDispatch, useSelector } from "react-redux";
import SelectJobModal from "./SelectJobModal";
import SelectSkillListingModal from "./SelectSkillListingModal";
import AIWriterPopover from "./AIWriterPopover";
import useAIWriter from "./useAIWriter";
import {
  createBid,
  createMultiBid,
  clearBidSucess,
  updateBid,
  clearUpdateBidSuccess,
} from "../../../../redux/actions";
import { alertNotification } from "../../../../components/TopNotification/alertNotification";
import TalentTypeTag from "../../../../components/TalentTypeTag/TalentTypeTag";
import moment from "moment";
import "./PlaceBidModal.css";
import { contractOptions, PAYROLL_PERCENTAGES, skillGigPercentage } from "../../../../shared/helpers";

const { Panel } = Collapse;

const INITIAL = {
  job: undefined,
  taxTerm: "w2",
  payPeriod: "hourly",
  payAmount: "",
  startDate: null,
  idealContractLengthId: undefined,
  message: "",
};

const BUDGET_TYPE_MAP = { hourly: "Hour", weekly: "Week" };
const MAX_PAY_AMOUNT = 99999999.99;

const getSkillListingLocation = (listing) =>
  listing?.SkillListings_Cities?.[0]?.Cities?.LocationWithCountry || "";

const getSkillListingEstimate = (listing) => {
  if (listing?.MinimumBid) return `$${listing.MinimumBid}/${listing.MinimumBidType || ""}`;
  if (listing?.ClientRate) return `$${listing.ClientRate}/${listing?.BudgetTypes?.BudgetTypeName || ""}`;
  return "";
};

const getSkillListingSubtitle = (listing) => {
  const parts = [getSkillListingLocation(listing), getSkillListingEstimate(listing)].filter(Boolean);
  return parts.length ? parts.join(" • ") : `#${listing.SkillListingId}`;
};

const calculateTalentNetPay = ({ amount, skillGigPercentage, payrollCostPercentage }) => {
  const skillGiggFee = amount * (skillGigPercentage / 100);
  const grossPay = amount - skillGiggFee;
  const payrollCost = grossPay * (payrollCostPercentage / 100);
  const netGrossPay = grossPay - payrollCost;

  return { skillGiggFee, grossPay, payrollCost, netGrossPay };
};

const getAlreadyPlacedBidItems = (payload) => {
  if (payload?.acceptedBidInfo) {
    return [payload.acceptedBidInfo];
  }
  const alreadyAcceptedBids = payload?.data?.alreadyAcceptedBids;
  return Array.isArray(alreadyAcceptedBids) ? alreadyAcceptedBids : [];
};

const formatBidDate = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : value;
};

const normalizeAlreadyPlacedBidRows = (payload) =>
  getAlreadyPlacedBidItems(payload).map((item, index) => ({
    id: item?.bidId || item?.skillListingId || `${index}`,
    candidate: item?.candidateName || "N/A",
    agent: item?.agentName || "N/A",
    dateContacted: formatBidDate(item?.bidPlacedAt) || "N/A",
  }));

const PlaceBidModal = ({ open, onClose, onBidSuccess, talent, talentType = "external", skillListingId, skillListingIds, skillListingOptions, editBidData, preselectedJob, incompleteJob = false, onCompleteJob, allowJobEdit = false }) => {
  const dispatch = useDispatch();
  const userProfile = JSON.parse(localStorage.getItem("userProfile"));

  const bidLoading = useSelector((state) => state.createBidReducers.loading);
  const bidError = useSelector((state) => state.createBidReducers.error);
  const bidErrorMessage = useSelector((state) => state.createBidReducers.errorMessage);
  const updateLoading = useSelector((state) => state.updateBidReducers.loading);

  const [f, setF] = useState(INITIAL);
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [skillListingModalOpen, setSkillListingModalOpen] = useState(false);
  const [selectedSkillListing, setSelectedSkillListing] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [responseModal, setResponseModal] = useState({
    open: false,
    mode: "single",
    rows: [],
    message: "",
  });

  const { generate, isGenerating } = useAIWriter();
  const msgWrapRef = useRef(null);
  const updateSessionRef = useRef(false);
  const submitModeRef = useRef("single");

  const activeSkillListingOptions = useMemo(
    () =>
      (skillListingOptions || []).filter((o) => {
        const statusName = o?.SkillListingStatuses?.SkillListingStatusName;
        return statusName !== "Deleted" && statusName !== "Expired" && statusName !== "Closed";
      }),
    [skillListingOptions]
  );
  const hasSkillListingOptions = activeSkillListingOptions.length > 0;

  useEffect(() => {
    if (open && hasSkillListingOptions) {
      setSelectedSkillListing(
        activeSkillListingOptions.length === 1 ? activeSkillListingOptions[0] : null
      );
    }
  }, [open, activeSkillListingOptions, hasSkillListingOptions]);

  const resolvedSkillListingId = hasSkillListingOptions
    ? selectedSkillListing?.SkillListingId
    : skillListingId;

  const handleClose = () => {
    dispatch(clearBidSucess());
    dispatch(clearUpdateBidSuccess());
    updateSessionRef.current = false;
    setF(INITIAL);
    setAiOpen(false);
    setSubmitError("");
    setSelectedSkillListing(null);
    setResponseModal({ open: false, mode: "single", rows: [], message: "" });
    onClose();
  };

  const handleResponseModalBack = () => {
    setResponseModal({ open: false, mode: "single", rows: [], message: "" });
    dispatch(clearBidSucess());
  };

  const handleResponseModalDone = () => {
    handleClose();
  };

  const handleCreateBidSuccess = ({ showAlert = false } = {}, responseData) => {
    const rows = normalizeAlreadyPlacedBidRows(responseData);
    if (rows.length) {
      onBidSuccess?.();
      setResponseModal({
        open: true,
        mode: "multi",
        rows,
        message: "",
      });
      return;
    }

    if (showAlert) {
      alertNotification("success", "Bid placed successfully");
    }
    onBidSuccess?.();
    dispatch(clearBidSucess());
    handleClose();
  };

  const handleCreateBidFailure = (errorData) => {
    setResponseModal({ open: false, mode: "single", rows: [], message: "" });
    setSubmitError(
      errorData?.errorMessage ||
        errorData?.message ||
        bidErrorMessage ||
        "Something went wrong. Please try again."
    );
  };

  const IDEAL_TERM_OPTIONS = useMemo(() => {
    return contractOptions.map((x) => ({
      label: x.IdealContractLengthName,
      value: x.IdealContractLengthId,
    }));
  }, []);

  useEffect(() => {
    if (open && editBidData) {
      const periodMap = { Hour: "hourly", Week: "weekly" };
      setF({
        job: editBidData.job || undefined,
        taxTerm: editBidData.taxTerm || "w2",
        payPeriod: periodMap[editBidData.budgetType] || "hourly",
        payAmount: editBidData.rate ? String(parseFloat(editBidData.rate) || "") : "",
        startDate: editBidData.startDate ? moment(editBidData.startDate) : null,
        idealContractLengthId: editBidData.idealTerm || undefined,
        message: editBidData.comments || "",
      });
    }
  }, [open, editBidData]);

  useEffect(() => {
    if (open && !editBidData && preselectedJob) {
      setF((prev) => ({ ...prev, job: preselectedJob }));
    }
  }, [open, editBidData, preselectedJob]);

  if (!talent) return null;

  const set = (key) => (val) => setF((prev) => ({ ...prev, [key]: val }));

  const handlePayAmountChange = (event) => {
    const value = event.target.value;
    if (value === "" || Number(value) <= MAX_PAY_AMOUNT) {
      set("payAmount")(value);
    }
  };

  const initials =
    talent.initials ||
    (talent.name || "")
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const amount = parseFloat(f.payAmount) || 0;
  const isCorpToCorp = f.taxTerm === "1099";

  const payrollCostPercentage = isCorpToCorp
    ? PAYROLL_PERCENTAGES.c2c[f.payPeriod]
    : PAYROLL_PERCENTAGES.w2[f.payPeriod];

  const { skillGiggFee, grossPay, payrollCost, netGrossPay } = calculateTalentNetPay({
    amount,
    skillGigPercentage,
    payrollCostPercentage,
  });

  const periodLabel = f.payPeriod === "hourly" ? "hr" : "wk";

  const jobTypeName = f.job?.JobOrderTypes?.JobOrderTypeName;
  const showPricingTransparency = jobTypeName ? jobTypeName !== "Permanent" : true;
  const isUSAJobLocation = f.job?.country === "United States";

  // ── Bid submission ───────────────────────────────────────────
  const isFormValid =
    f.job &&
    Number(f.payAmount) > 0 &&
    Number(f.payAmount) <= MAX_PAY_AMOUNT &&
    f.startDate &&
    f.idealContractLengthId &&
    f.message?.trim() &&
    (resolvedSkillListingId || skillListingIds?.length);

  const handleSubmit = () => {
    if (hasSkillListingOptions && !resolvedSkillListingId) return setSubmitError("Please select a skill listing.");
    if (!f.job) return setSubmitError("Please select a job.");
    if (!amount || amount <= 0) return setSubmitError("Please enter a valid pay rate.");
    if (amount > MAX_PAY_AMOUNT) return setSubmitError("Pay rate cannot exceed $9,999,999,999.99.");
    if (!f.message.trim()) return setSubmitError("Please write a message for the talent.");
    if (!skillListingIds?.length && !resolvedSkillListingId) return setSubmitError("Skill listing not found. Please refresh and try again.");

    setSubmitError("");

    const rateType = BUDGET_TYPE_MAP[f.payPeriod] || "Hour";
    const taxTermName = f.taxTerm === "w2" ? "W-2" : "1099/Corp-to-Corp";

    const isCorpContract = f.job?.JobOrderTypes?.JobOrderTypeCode !== "P";
    const noFees = userProfile?.Profiles?.Role === "No billing";
    const sgFees = isCorpContract && !noFees ? 0.6899 : null;

    const actualRate = isCorpContract && !noFees ? Number(netGrossPay).toFixed(2) : amount;

    const permanentJob = f.job?.JobOrderTypes?.JobOrderTypeName !== "Contract";
    const startDateStr = f.startDate ? f.startDate.toISOString() : "";
    const idealTermId = f.idealContractLengthId ?? null;

    const isBulk = skillListingIds && skillListingIds.length > 1;
    const singleId = skillListingIds?.length === 1 ? skillListingIds[0] : resolvedSkillListingId;

    if (editBidData) {
      submitModeRef.current = "single";
      updateSessionRef.current = true;
      dispatch(updateBid({
        id: editBidData.bidId,
        GigId: f.job.JobOrderId,
        SGFees: sgFees,
        ActualRate: String(actualRate),
        SkillListingId: singleId,
        Comments: f.message,
        BudgetType: rateType,
        BidStatusName: "New",
        Rate: String(Number(amount).toFixed(2)),
        IdealTerm: idealTermId,
        IdealStartDate: startDateStr,
        Message: f.message + "\nBid Amount: " + Number(amount).toFixed(2),
        loading: "Submitted",
        IdealTermsName: taxTermName,
        onSuccess: () => {
          updateSessionRef.current = false;
          onBidSuccess?.();
          dispatch(clearUpdateBidSuccess());
          handleClose();
        },
      }));
    } else if (isBulk) {
      submitModeRef.current = "multi";
      dispatch(createMultiBid(
        {
          bids: skillListingIds.map((id) => ({
            GigId: f.job.JobOrderId,
            SGFees: sgFees,
            ActualRate: String(actualRate),
            SkillListingId: id,
            Comments: f.message,
            BudgetType: rateType,
            Message: f.message + "\nBid Amount: " + Number(amount).toFixed(2),
            BidStatusName: "New",
            Rate: String(Number(amount).toFixed(2)),
            IdealTerm: idealTermId,
            IdealStartDate: startDateStr,
            IdealTermsName: taxTermName,
          })),
        },
        (responseData) => handleCreateBidSuccess({}, responseData)
      ));
    } else {
      submitModeRef.current = "single";
      dispatch(
        createBid(
          f.job.JobOrderId,
          sgFees,
          actualRate,
          singleId,
          f.message,
          rateType,
          f.message + "\nBid Amount: " + amount,
          "New",
          amount,
          idealTermId,
          startDateStr,
          permanentJob,
          taxTermName,
          (responseData) => handleCreateBidSuccess({ showAlert: true }, responseData),
          handleCreateBidFailure
        )
      );
    }
  };

  const handleGenerate = (tone) => {
    setAiOpen(false);
    generate({
      talentName: talent.name ?? "the talent",
      talentTitle: talent.title ?? "",
      jobTitle: f.job?.title ?? "",
      tone,
      onChunk: (text) => setF((prev) => ({ ...prev, message: text })),
      onDone: (text) => setF((prev) => ({ ...prev, message: text })),
    });
  };

  const formatLocation = (job) => {
    if (!job) return "";
    if (job.location) return job.location;
    const city = job.city ?? "";
    const state = job.state ?? "";
    return `${city}${city && state ? ", " : ""}${state}`.trim();
  };

  return (
    <>
    <Modal
      open={open && !jobModalOpen && !skillListingModalOpen}
      onCancel={handleClose}
      footer={null}
      closable={false}
      className="pbm-modal"
      width="min(625px, 95vw)"
      centered
      destroyOnClose
    >
      {/* Header */}
      <div className="pbm-header">
        <span className="pbm-title">{editBidData ? "Edit Bid" : "Place a Bid"}</span>
        <button className="pbm-close-btn" type="button" onClick={handleClose}>
          <CloseOutlined />
        </button>
      </div>
      <div className="pbm-divider" />

      {/* Talent row */}
      <div className="pbm-talent-row">
        <div className="pbm-avatar">
          {talent.avatar ? (
            <img src={talent.avatar} alt={talent.name} className="pbm-avatar-img" />
          ) : (
            <span className={`pbm-avatar-initials pbm-avatar-initials-${talentType}`}>{initials}</span>
          )}
          {talent.online && <span className="pbm-online-dot" />}
        </div>

        <div className="pbm-talent-info">
          <span className="pbm-talent-name">{talent.name}</span>
          <span className="pbm-talent-title">{talent.title}</span>
        </div>

        <div className="pbm-talent-meta">
          {talent.aiScore != null && (
            <span className="pbm-ai-score">
              <span className="pbm-ai-star">✦</span> AI Score {talent.aiScore}%
            </span>
          )}
          <TalentTypeTag type={talentType} />
        </div>
      </div>

      {/* Select Skill Listing */}
      {hasSkillListingOptions && (
        <div className="pbm-section">
          <label className="pbm-label">
            Select a Skill Listing <span className="pbm-required">*</span>
          </label>

          <button
            type="button"
            className={`job-btn ${selectedSkillListing ? "is-selected" : "is-empty"}`}
            onClick={() => activeSkillListingOptions.length > 1 && setSkillListingModalOpen(true)}
          >
            <div className="job-main">
              {!selectedSkillListing && <img src={SelectJobIcon} alt="" className="job-icon" />}

              {selectedSkillListing ? (
                <div className="job-content">
                  <div className="job-title">{selectedSkillListing.Title || "Untitled"}</div>
                  <div className="job-subtitle">{getSkillListingSubtitle(selectedSkillListing)}</div>
                </div>
              ) : (
                <div className="job-placeholder">Select a Skill Listing</div>
              )}
            </div>

            {selectedSkillListing && activeSkillListingOptions.length > 1 && (
              <div className="job-action" aria-hidden="true">
                <img src={EditIcon} alt="EditIcon" />
              </div>
            )}
          </button>
        </div>
      )}

      {/* Select Job */}
      <div className="pbm-section">
        {incompleteJob && (
          <div className="sjm-incomplete-banner" style={{ marginBottom: 12 }}>
            <WarningOutlined className="sjm-incomplete-icon" />
            This job is missing some required details. Please complete the job before placing a bid.
          </div>
        )}
        <label className="pbm-label">
          Select Job <span className="pbm-required">*</span>
        </label>

        <button
          type="button"
          className={`job-btn ${f.job ? "is-selected" : "is-empty"}`}
          onClick={() => (!preselectedJob || allowJobEdit) && setJobModalOpen(true)}
          disabled={Boolean(preselectedJob) && !allowJobEdit}
        >
          <div className="job-main">
            {!f.job && <img src={SelectJobIcon} alt="" className="job-icon" />}

            {f.job ? (
              <div className="job-content">
                <div className="job-title">{f.job.title}</div>
                <div className="job-subtitle">{formatLocation(f.job)}</div>
              </div>
            ) : (
              <div className="job-placeholder">Select a Job</div>
            )}
          </div>

          {f.job && (!preselectedJob || allowJobEdit) && (
            <div className="job-action" aria-hidden="true">
              <img src={EditIcon} alt="EditIcon" />
            </div>
          )}
        </button>

      </div>

      {/* Tax Term */}
      {isUSAJobLocation && (
        <div className="pbm-section">
          <label className="pbm-label">Tax Term</label>
          <div className="pbm-radio-group">
            {[
              { value: "w2", label: "W-2" },
              { value: "1099", label: "1099 from Corp-to-Corp" },
            ].map(({ value, label }) => (
              <label key={value} className="pbm-radio-item">
                <input
                  type="radio"
                  name="pbm-taxTerm"
                  value={value}
                  checked={f.taxTerm === value}
                  onChange={() => set("taxTerm")(value)}
                />
                <span className={`pbm-radio-label ${f.taxTerm === value ? "active" : ""}`}>{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Pay Rate */}
      <div className="pbm-section">
        <label className="pbm-label">
          Add Estimated Pay Rate <span className="pbm-required">*</span>
        </label>
        <div className="pbm-radio-group" style={{ marginBottom: 12 }}>
          {[
            { value: "hourly", label: "Hourly" },
            { value: "weekly", label: "Weekly" },
          ].map(({ value, label }) => (
            <label key={value} className="pbm-radio-item">
              <input
                type="radio"
                name="pbm-payPeriod"
                value={value}
                checked={f.payPeriod === value}
                onChange={() => set("payPeriod")(value)}
              />
              <span className={`pbm-radio-label ${f.payPeriod === value ? "active" : ""}`}>{label}</span>
            </label>
          ))}
        </div>
        <div className="pbm-amount-box">
          <span className="pbm-dollar-icon">$</span>
          <input
            type="number"
            className="pbm-amount-input"
            placeholder="0"
            min={0}
            max={MAX_PAY_AMOUNT}
            step="0.01"
            value={f.payAmount}
            onChange={handlePayAmountChange}
          />
        </div>
      </div>

      {/* Pricing Transparency */}
      {showPricingTransparency && (
        <>
          <Collapse ghost className="pbm-collapse" defaultActiveKey={["pricing"]}  expandIconPosition="end">
            <Panel header={<span className="pbm-label">Pricing Transparency</span>} key="pricing">
              {amount > 0 ? (
                <div className="pbm-pricing-details">
                  <div className="pbm-pricing-row">
                    <span className="pbm-pricing-key">Final Bill Rate</span>
                    <span className="pbm-pricing-val">
                      ${Number(amount).toFixed(2)}/{periodLabel}
                    </span>
                  </div>

                  <div className="pbm-pricing-row">
                    <span className="pbm-pricing-key">SkillGigs Fees ({skillGigPercentage}%)</span>
                    <span className="pbm-pricing-val pbm-pricing-deduct">
                      (${Number(skillGiggFee).toFixed(2)})
                    </span>
                  </div>

                  <div className="pbm-pricing-row">
                    <span className="pbm-pricing-key">Talent Gross Pay</span>
                    <span className="pbm-pricing-val">${Number(grossPay).toFixed(2)}</span>
                  </div>

                  <div className="pbm-pricing-row">
                    <span className="pbm-pricing-key">
                      Payroll Cost to Talent ({payrollCostPercentage}%)
                    </span>
                    <span className="pbm-pricing-val pbm-pricing-deduct">
                      (${Number(payrollCost).toFixed(2)})
                    </span>
                  </div>

                  <div className="pbm-pricing-row pbm-pricing-net">
                    <span className="pbm-pricing-key">Talent Net Pay</span>
                    <span className="pbm-pricing-val">
                      ${Number(netGrossPay).toFixed(2)}/{periodLabel}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="pbm-pricing-placeholder">Enter a pay rate above to see the pricing breakdown.</p>
              )}
            </Panel>
          </Collapse>
          <div className="pbm-divider" />
        </>
      )}

      {/* Start Date + Contract Length */}
      <div className="pbm-two-col">
        <div className="pbm-col">
          <label className="pbm-label">
            Start Date <span className="pbm-required">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={f.startDate}
            onChange={set("startDate")}
            format="MMM DD, YYYY"
            className="pbm-datepicker"
          />
        </div>

        <div className="pbm-col">
          <label className="pbm-label">
            Contract Length <span className="pbm-required">*</span>
          </label>
          <Select
            className="pbm-contract-select"
            style={{ width: "100%" }}
            placeholder="Select"
            value={f.idealContractLengthId}
            onChange={set("idealContractLengthId")}
            options={IDEAL_TERM_OPTIONS}
            allowClear
          />
        </div>
      </div>

      {/* Message */}
      <div className="pbm-section pbm-msg-wrap" ref={msgWrapRef}>
        <div className="pbm-msg-header-row">
          <label className="pbm-label">
            Write Message For Talent <span className="pbm-required">*</span>
          </label>
          {/* <button className="pbm-ai-write-btn" type="button" onClick={() => setAiOpen((v) => !v)}>
            <span className="pbm-ai-star">✦</span> Write with the AI
          </button> */}
          {aiOpen && <AIWriterPopover isGenerating={isGenerating} onGenerate={handleGenerate} />}
        </div>

        <textarea
          className="pbm-textarea"
          placeholder="Write a message to the talent to invite him."
          value={f.message}
          maxLength={200}
          rows={4}
          onChange={(e) => set("message")(e.target.value)}
        />
        <div className="pbm-char-count">{f.message.length}/200</div>
      </div>

      {/* Error */}
      {(submitError || bidError) && (
        <p className="pbm-submit-error">{submitError || bidErrorMessage || "Something went wrong. Please try again."}</p>
      )}

      {/* Submit */}
      {incompleteJob ? (
        <button className="pbm-submit-btn" type="button" onClick={onCompleteJob}>
          Complete Job Details <ArrowRightOutlined style={{ marginLeft: 8 }} />
        </button>
      ) : (
        <button className="pbm-submit-btn" type="button" onClick={handleSubmit} disabled={!isFormValid || bidLoading || updateLoading}>
          {bidLoading ? "Submitting..." : "Submit Bid \u2192"}
        </button>
      )}
    </Modal>
    <Modal
      open={responseModal.open}
      footer={null}
      closable={false}
      centered
      destroyOnClose
      width="min(824px, 95vw)"
      className="pbm-response-modal"
      zIndex={1020}
    >
      <div className="pbm-response-content">
        <div className="pbm-response-copy">
          {responseModal.mode === "multi" ? (
            <>
              <h2 className="pbm-response-title">Some Candidates Couldn't Receive Your Bid</h2>
              <p className="pbm-response-text">
                The following candidates won't receive your bid because they already have a bid pending from another agent.
              </p>
            </>
          ) : (
            <p className="pbm-response-text">
              {responseModal.message || "This candidate couldn't receive your bid."}
            </p>
          )}
          <p className="pbm-response-text">Details below.</p>
        </div>

        {responseModal.rows.length ? (
          <div className="pbm-response-table-wrap">
            <table className="pbm-response-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Agent</th>
                  <th>Date Contacted</th>
                </tr>
              </thead>
              <tbody>
                {responseModal.rows.map((row) => (
                  <tr key={row.id}>
                    <td title={row.candidate}>{row.candidate}</td>
                    <td title={row.agent}>{row.agent}</td>
                    <td>{row.dateContacted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="pbm-response-empty">{responseModal.message}</p>
        )}

        <button
          className="pbm-response-btn"
          type="button"
          onClick={responseModal.mode === "multi" ? handleResponseModalDone : handleResponseModalBack}
        >
          {responseModal.mode === "multi" ? "Done" : "Back"}
        </button>
      </div>
    </Modal>
    <SelectJobModal
      open={jobModalOpen}
      selectedJob={f.job}
      onClose={() => setJobModalOpen(false)}
      onSelect={(job) => {
        set("job")(job);
        setJobModalOpen(false);
      }}
      bidContext={{ talent, talentType, skillListingId, skillListingIds }}
      showVmsFacilityFilter={talentType !== "marketplace"}
      checkIncomplete
      autoSelectJob={editBidData ? null : preselectedJob}
    />
    {hasSkillListingOptions && (
      <SelectSkillListingModal
        open={skillListingModalOpen}
        options={activeSkillListingOptions}
        selected={selectedSkillListing}
        talent={talent}
        onClose={() => setSkillListingModalOpen(false)}
        onSelect={(listing) => {
          setSelectedSkillListing(listing);
          setSkillListingModalOpen(false);
        }}
        zIndex={1010}
      />
    )}
    </>
  );
};

export default PlaceBidModal;
